/**
 * §5.8 前台自动确认待下单结果：只 resolve，不重发旧订单。
 * 后台暂停；回到前台继续。成功后刷新普通/AI 持仓与资产。
 */
import {
  confirmPendingOrderSubmission,
  hasPendingOrderRecovery,
} from '@/api/position';
import { getCurrentWalletAddress } from '@/common/services/request';
import logger from '@/common/utils/logger';
import type { Order } from '@/api/orders';

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = true;
let inFlight = false;
let backoffMs = 2000;

const MAX_BACKOFF_MS = 30_000;
const BASE_MS = 2000;

function clearTimer() {
  if (timer != null) {
    clearTimeout(timer);
    timer = null;
  }
}

async function refreshTradeStores(order: Order, wallet: string) {
  try {
    const { usePositionStore } = await import('@/store/position');
    const { useAIPredictionPositionStore } = await import(
      '@/store/aiPredictionPosition'
    );
    const { useWalletStore } = await import('@/store/wallet');
    const positionStore = usePositionStore();
    const aiStore = useAIPredictionPositionStore();
    if ((getCurrentWalletAddress() || '').toLowerCase().trim() !== wallet.toLowerCase().trim()) return;
    positionStore.upsertAcceptedOrder(order, wallet);
    aiStore.upsertAcceptedOrder(order, wallet);
    void Promise.allSettled([
      positionStore.refreshPositions(0),
      positionStore.refreshHistoryPositions(0),
      aiStore.refreshPositions(),
      aiStore.refreshHistoryPositions(),
      useWalletStore().fetchUserAssets(true),
    ]);
  } catch (e) {
    logger.warn('[OrderAutoConfirm] refresh stores failed', e);
  }
}

async function tick() {
  if (stopped || inFlight) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return;
  }

  const wallet = getCurrentWalletAddress();
  if (!wallet || !hasPendingOrderRecovery(wallet)) {
    backoffMs = BASE_MS;
    schedule();
    return;
  }

  inFlight = true;
  try {
    const result = await confirmPendingOrderSubmission(wallet);
    if (result.outcome === 'accepted') {
      backoffMs = BASE_MS;
      await refreshTradeStores(result.order, wallet);
      if ((getCurrentWalletAddress() || '').toLowerCase().trim() !== wallet.toLowerCase().trim()) return;
      uni.showToast({
        title: '下单成功',
        icon: 'success',
      });
    } else if (result.outcome === 'closed') {
      backoffMs = BASE_MS;
      uni.showToast({
        title: '本次下单未成交，未扣款',
        icon: 'error',
        duration: 2800,
      });
    } else if (result.outcome === 'pending' || result.outcome === 'aborted') {
      backoffMs = Math.min(MAX_BACKOFF_MS, Math.round(backoffMs * 1.5));
    } else {
      backoffMs = BASE_MS;
    }
  } catch (e) {
    logger.warn('[OrderAutoConfirm] resolve failed', e);
    backoffMs = Math.min(MAX_BACKOFF_MS, Math.round(backoffMs * 1.5));
  } finally {
    inFlight = false;
    schedule();
  }
}

function schedule() {
  clearTimer();
  if (stopped) return;
  timer = setTimeout(() => {
    void tick();
  }, backoffMs);
}

/** App 前台：启动自动确认循环 */
export function startOrderAutoConfirm() {
  stopped = false;
  backoffMs = BASE_MS;
  clearTimer();
  // 立即尝试一次，再进入间隔
  void tick();
}

/** App 后台：停止调度 */
export function stopOrderAutoConfirm() {
  stopped = true;
  clearTimer();
}
